import React from 'react';
import { createPortal } from 'react-dom';
import {
  Autocomplete, Box, Button, Checkbox, Chip, IconButton, InputAdornment, MenuItem, Paper,
  TextField, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useHeaderSlot } from '../../layouts/dashboard/headerSlot';

// Toolbar for the admin card dashboards. Pair with useDashboardFilters, which
// supplies everything except `noun`, `shown`, `total`, `active` and `onClear`.
// From tablet width up it is pinned in the admin header (replacing the default
// search); on phones it sits at the top of the page instead.
export default function DashboardFilterBar(props) {
  const theme = useTheme();
  const canPin = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const headerNode = useHeaderSlot(canPin);

  if (headerNode) {
    return createPortal(
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, py: 1.5 }}>
        <FilterControls {...props} compact />
      </Box>,
      headerNode
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}
    >
      <FilterControls {...props} />
    </Paper>
  );
}

function FilterControls({
  noun,
  query,
  onQueryChange,
  filters,
  sorts,
  sortKey,
  onSortChange,
  shown,
  total,
  active,
  onClear,
  compact = false,
}) {
  const fieldWidth = compact ? { xs: '100%', sm: 170 } : { xs: '100%', sm: 220 };

  return (
    <>
      <TextField
        size="small"
        placeholder={`Search ${noun}`}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        sx={{ width: compact ? { xs: '100%', sm: 220 } : { xs: '100%', sm: 260 } }}
        inputProps={{ 'aria-label': `Search ${noun}` }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Clear search" onClick={() => onQueryChange('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {filters.map((filter) => (
        <Autocomplete
          key={filter.key}
          multiple
          size="small"
          disableCloseOnSelect
          limitTags={1}
          options={filter.options}
          value={filter.options.filter((o) => filter.selected.includes(o.value))}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          getOptionLabel={(o) => o.label}
          onChange={(e, picked) => filter.onChange(picked.map((o) => o.value))}
          noOptionsText="No options"
          sx={{ width: fieldWidth }}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.value}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0 }} />
              <Box component="span" sx={{ flex: 1 }}>
                {option.label}
              </Box>
              <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                {option.count}
              </Typography>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip size="small" label={option.label} {...getTagProps({ index })} key={option.value} />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label={filter.label} placeholder={filter.selected.length ? '' : 'Any'} />
          )}
        />
      ))}

      <TextField
        select
        size="small"
        label="Sort"
        value={sortKey}
        onChange={(e) => onSortChange(e.target.value)}
        sx={{ width: fieldWidth }}
      >
        {sorts.map((s) => (
          <MenuItem key={s.key} value={s.key}>
            {s.label}
          </MenuItem>
        ))}
      </TextField>

      <Box sx={{ ml: { sm: 'auto' }, display: 'flex', alignItems: 'center', gap: 1 }}>
        {total > 0 && (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            aria-live="polite"
          >
            Showing {shown} of {total}
          </Typography>
        )}
        {active && (
          <Button size="small" onClick={onClear} sx={{ whiteSpace: 'nowrap' }}>
            Clear filters
          </Button>
        )}
      </Box>
    </>
  );
}

export function DashboardNoMatches({ noun, onClear }) {
  return (
    <Box sx={{ textAlign: 'center', mt: 4 }}>
      <Typography>No {noun} match these filters.</Typography>
      <Button sx={{ mt: 1 }} onClick={onClear}>
        Clear filters
      </Button>
    </Box>
  );
}
